import {
    getCoreSkillCount,
    getSkill,
    getSkillVersions,
    isLiveCoreSkill,
    publishSkill,
    type ISkill,
} from "@/api/skills";
import {
    useMutation,
    useQuery,
    useQueryClient,
    useSuspenseQuery,
} from "@tanstack/react-query";
import { organizationGroupQueryKeys } from "@/api/organization-groups";
import { isAxiosError } from "axios";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router";

import {
    SkillEditorSaveConflictError,
    SkillEditorSaveRecoveryRequiredError,
    useSkillEditorSaveExecution,
    type OwnedSkillEditorSaveConflict,
    type OwnedSkillEditorSaveRecovery,
    type SkillEditorSaveFailureContext,
    type SkillEditorSaveResult,
} from "./useSkillEditorSaveExecution";

import {
    deriveSkillEditorReferences,
    getSkillEditorBrokenTargets,
    getSkillEditorInboundSources,
    getSkillEditorReferenceCount,
    getSkillEditorRenameReferenceCount,
    type SkillEditorReferences,
} from "../skill-editor-references";
import {
    applySkillEditorOperationReceipt,
    createSkillEditorRetryBlockedRecovery,
} from "../skill-editor-save-conflicts";
import {
    canRetrySkillEditorPublishOnly,
    evaluateSettledSkillEditorPublishFailure,
    planSkillEditorPublishOnlyRetry,
    reconcileSkillEditorUnknownPublication,
    type SkillEditorPublishOnlyRetryPlan,
    type SkillEditorPublishRetryWarningAcknowledgment,
} from "../skill-editor-publication";
import {
    reconcileSkillEditorSaveFailure,
    reloadSkillEditorConflicts,
    reloadSkillEditorRecoveryFile,
} from "../skill-editor-save-reconciliation";
import {
    SkillEditorSaveValidationError,
    validateSkillEditorState,
    type SkillEditorSaveIntent,
    type SkillEditorSaveRecovery,
    type SkillEditorSaveStatus,
} from "../skill-editor-save";
import {
    acknowledgeSkillEditorRename,
    addSkillEditorFile,
    cancelSkillEditorRename,
    clearSkillEditorReviewRequired,
    createSkillEditorState,
    deleteSkillEditorFile,
    deriveSkillEditorDiff,
    discardSkillEditorChanges,
    getSkillEditorDisplayFiles,
    getSkillEditorKey,
    getSkillEditorScopeAssignment,
    isSkillEditorDirty,
    isSkillIndexField,
    isSkillSettingsField,
    normalizeSkillEditorText,
    requestSkillEditorRenameCommit,
    reseedSkillEditorState,
    resetSkillEditorState,
    selectSkillEditorFile,
    updateSkillEditorFields,
    updateSkillEditorFile,
    type SkillEditorFields,
    type SkillEditorFile,
    type SkillEditorState,
} from "../skill-editor-state";

export {
    SkillEditorSaveConflictError,
    SkillEditorSaveRecoveryRequiredError,
} from "./useSkillEditorSaveExecution";
export type {
    SkillEditorSaveConflictView,
    SkillEditorSaveResult,
} from "./useSkillEditorSaveExecution";

export {
    isSkillIndexField,
    isSkillSettingsField,
    SKILL_EDITOR_FIELD_GROUP,
} from "../skill-editor-state";

type StateTransition = (state: SkillEditorState) => SkillEditorState;

export interface SkillEditorSaveChange {
    readonly id: string;
    readonly label: string;
}

export const deriveSkillEditorSaveChanges = (
    state: SkillEditorState
): readonly SkillEditorSaveChange[] => {
    const diff = deriveSkillEditorDiff(state);
    const changes: SkillEditorSaveChange[] = [];

    if (diff.skillFields.some(isSkillIndexField)) {
        changes.push({ id: "skill-content", label: "SKILL.md — edited" });
    }
    if (diff.skillFields.some(isSkillSettingsField)) {
        changes.push({
            id: "skill-settings",
            label: "Skill settings — edited",
        });
    }
    for (const file of diff.deletedFiles) {
        changes.push({
            id: `${file.clientKey}:removed`,
            label: `${file.name} — removed`,
        });
    }
    for (const { file, changedFields } of diff.updatedFiles) {
        const name = normalizeSkillEditorText(file.name);
        if (changedFields.includes("name")) {
            changes.push({
                id: `${file.clientKey}:renamed`,
                label: `${name} — renamed`,
            });
        }
        if (changedFields.includes("content")) {
            changes.push({
                id: `${file.clientKey}:edited`,
                label: `${name} — edited`,
            });
        }
    }
    for (const file of diff.addedFiles) {
        changes.push({
            id: `${file.clientKey}:added`,
            label: `${normalizeSkillEditorText(file.name)} — new file`,
        });
    }

    return changes;
};

const getSkillEditorErrorReason = (error: unknown): string | null => {
    if (isAxiosError<{ detail?: unknown }>(error)) {
        const detail = error.response?.data?.detail;
        return typeof detail === "string" ? detail : null;
    }
    return error instanceof Error ? error.message : null;
};

interface PendingSkillEditorSave {
    skillId: number;
    routeEpoch: number;
    intent: SkillEditorSaveIntent;
    promise: Promise<SkillEditorSaveResult>;
}

interface PendingSkillEditorPublishRetry {
    skillId: number;
    routeEpoch: number;
    promise: Promise<SkillEditorPublishRetryResult | null>;
}

export type SkillEditorPublishRetryResult =
    | { readonly kind: "applied"; readonly skill: ISkill | null }
    | { readonly kind: "rejected"; readonly reason: string | null };

const mutationInvalidationKeys = [
    ["admin-skills"],
    ["admin-skills-all"],
    ["admin-skills-core-count"],
] as const;

export const useStableStringList = <Values extends readonly string[]>(
    values: Values
): Values => {
    const stableValues = useRef(values);
    if (
        stableValues.current.length !== values.length ||
        values.some((value, index) => value !== stableValues.current[index])
    ) {
        stableValues.current = values;
    }
    return stableValues.current;
};

const hasLocalEditorState = (state: SkillEditorState) =>
    isSkillEditorDirty(state) ||
    state.buffer.files.some(({ serverId }) => serverId === null);

const preserveSkillEditorClientKeys = (
    previous: SkillEditorState,
    replacement: SkillEditorState
): SkillEditorState => {
    if (previous.skillId !== replacement.skillId) return replacement;
    const clientKeyByServerId = new Map(
        previous.baseline.files.map(({ serverId, clientKey }) => [
            serverId,
            clientKey,
        ])
    );
    const baselineFiles = replacement.baseline.files.map((file) => ({
        ...file,
        clientKey: clientKeyByServerId.get(file.serverId) ?? file.clientKey,
    }));
    const bufferFiles = replacement.buffer.files.map((file) => ({
        ...file,
        clientKey:
            file.serverId === null
                ? file.clientKey
                : (clientKeyByServerId.get(file.serverId) ?? file.clientKey),
    }));
    const reconciled = {
        ...replacement,
        baseline: { ...replacement.baseline, files: baselineFiles },
        buffer: { ...replacement.buffer, files: bufferFiles },
    };
    if (replacement.selectedClientKey === previous.selectedClientKey) {
        return reconciled;
    }
    const selectedFile = bufferFiles.find(
        ({ clientKey }) => clientKey === previous.selectedClientKey
    );
    if (!selectedFile) return reconciled;
    const previousSelectedFile = previous.buffer.files.find(
        ({ clientKey }) => clientKey === previous.selectedClientKey
    );
    return {
        ...reconciled,
        selectedClientKey: previous.selectedClientKey,
        resetEpoch:
            previousSelectedFile?.content === selectedFile.content
                ? replacement.resetEpoch
                : replacement.resetEpoch + 1,
    };
};

const preserveIdlessSkillEditorFiles = (
    previous: SkillEditorState,
    replacement: SkillEditorState
): SkillEditorState => {
    if (previous.skillId !== replacement.skillId) return replacement;
    const replacementKeys = new Set(
        replacement.buffer.files.map(({ clientKey }) => clientKey)
    );
    const idlessFiles = previous.buffer.files.filter(
        ({ clientKey, serverId }) =>
            serverId === null && !replacementKeys.has(clientKey)
    );
    if (idlessFiles.length === 0) return replacement;
    const files = [...replacement.buffer.files, ...idlessFiles];
    const selectedClientKey = files.some(
        ({ clientKey }) => clientKey === previous.selectedClientKey
    )
        ? previous.selectedClientKey
        : replacement.selectedClientKey;
    return {
        ...replacement,
        buffer: { ...replacement.buffer, files },
        selectedClientKey,
    };
};

export const useSkillEditor = (skillId: string) => {
    const queryClient = useQueryClient();
    const location = useLocation();
    const navigate = useNavigate();
    const numericSkillId = Number(skillId);
    const detailsQueryKey = useMemo(
        () => ["admin-skill-details", skillId] as const,
        [skillId]
    );
    const justDuplicated = Boolean(
        (location.state as { justDuplicated?: boolean } | null)?.justDuplicated
    );
    const { data: skill } = useSuspenseQuery({
        queryKey: detailsQueryKey,
        queryFn: () => getSkill(numericSkillId),
    });
    const [state, setState] = useState(() =>
        createSkillEditorState(skill, { reviewRequired: justDuplicated })
    );
    const stateRef = useRef(state);
    const lastSourceRef = useRef(skill);
    const routeSkillIdRef = useRef(skill.id);
    const routeEpochRef = useRef(0);
    const pendingSourceRef = useRef<ISkill | null>(null);
    const pendingSaveRef = useRef<PendingSkillEditorSave | null>(null);
    const pendingPublishRetryRef =
        useRef<PendingSkillEditorPublishRetry | null>(null);
    const [pendingSaveSettlement, setPendingSaveSettlement] = useState(0);
    const pendingSnapshotSkillIdsRef = useRef<ReadonlySet<number>>(new Set());
    const [pendingSnapshotSkillIds, setPendingSnapshotSkillIds] = useState<
        ReadonlySet<number>
    >(() => new Set());
    const saveConflictRef = useRef<OwnedSkillEditorSaveConflict | null>(null);
    const [ownedSaveConflict, setOwnedSaveConflict] =
        useState<OwnedSkillEditorSaveConflict | null>(null);
    const saveRecoveryRef = useRef<OwnedSkillEditorSaveRecovery | null>(null);
    const [ownedSaveRecovery, setOwnedSaveRecovery] =
        useState<OwnedSkillEditorSaveRecovery | null>(null);
    const acknowledgedConflictRef = useRef<{
        readonly skillId: number;
        readonly routeEpoch: number;
        readonly token: string;
    } | null>(null);
    const [ownedSaveStatus, setOwnedSaveStatus] = useState<{
        readonly skillId: number;
        readonly routeEpoch: number;
        readonly status: SkillEditorSaveStatus;
    } | null>(null);
    const [recoveryResolutionOwner, setRecoveryResolutionOwner] = useState<{
        readonly skillId: number;
        readonly routeEpoch: number;
    } | null>(null);
    const clearedLocationKeyRef = useRef<string | null>(null);
    const referencesByStateRef = useRef(
        new WeakMap<SkillEditorState, SkillEditorReferences>()
    );
    const referencesForState = useCallback((candidate: SkillEditorState) => {
        const cached = referencesByStateRef.current.get(candidate);
        if (cached) return cached;
        const derived = deriveSkillEditorReferences(candidate);
        referencesByStateRef.current.set(candidate, derived);
        return derived;
    }, []);
    const derivedReferences = referencesForState(state);
    const recognitionVocabulary = useStableStringList([
        ...derivedReferences.vocabulary,
    ]);
    const references = useMemo<SkillEditorReferences>(
        () =>
            derivedReferences.vocabulary === recognitionVocabulary
                ? derivedReferences
                : { ...derivedReferences, vocabulary: recognitionVocabulary },
        [derivedReferences, recognitionVocabulary]
    );
    const validation = useMemo(
        () => validateSkillEditorState(state, references),
        [references, state]
    );
    const diff = useMemo(() => deriveSkillEditorDiff(state), [state]);
    const saveChanges = useMemo(
        () => deriveSkillEditorSaveChanges(state),
        [state]
    );
    const hasDiscardableChanges = isSkillEditorDirty(state);
    const hasPendingSnapshot = pendingSnapshotSkillIds.has(state.skillId);
    const saveConflict =
        ownedSaveConflict?.skillId === state.skillId &&
        ownedSaveConflict.routeEpoch === routeEpochRef.current
            ? ownedSaveConflict.conflict
            : null;
    const saveRecovery =
        ownedSaveRecovery?.skillId === state.skillId &&
        ownedSaveRecovery.routeEpoch === routeEpochRef.current
            ? ownedSaveRecovery.recovery
            : null;
    const saveStatus =
        ownedSaveStatus?.skillId === state.skillId &&
        ownedSaveStatus.routeEpoch === routeEpochRef.current
            ? ownedSaveStatus.status
            : ({ kind: "idle" } satisfies SkillEditorSaveStatus);
    const isResolvingRecovery =
        recoveryResolutionOwner?.skillId === state.skillId &&
        recoveryResolutionOwner.routeEpoch === routeEpochRef.current;
    const hasLocalState = hasLocalEditorState(state);
    const isDirty = hasDiscardableChanges || hasPendingSnapshot;

    const transition = useCallback((apply: StateTransition) => {
        const next = apply(stateRef.current);
        stateRef.current = next;
        setState(next);
    }, []);

    const cacheAuthoritativeSkill = useCallback(
        (source: ISkill, routeEpoch: number) => {
            const cached = queryClient.setQueryData<ISkill>(
                ["admin-skill-details", String(source.id)],
                source
            );
            if (
                stateRef.current.skillId === source.id &&
                routeEpochRef.current === routeEpoch
            ) {
                lastSourceRef.current = cached ?? source;
                pendingSourceRef.current = null;
            }
        },
        [queryClient]
    );

    const storeSaveConflict = useCallback(
        (conflict: OwnedSkillEditorSaveConflict | null) => {
            saveConflictRef.current = conflict;
            setOwnedSaveConflict(conflict);
        },
        []
    );
    const storeSaveRecovery = useCallback(
        (recovery: OwnedSkillEditorSaveRecovery | null) => {
            saveRecoveryRef.current = recovery;
            setOwnedSaveRecovery(recovery);
        },
        []
    );
    const storeSaveStatus = useCallback(
        (
            status: SkillEditorSaveStatus,
            skillId: number,
            routeEpoch: number
        ) => {
            setOwnedSaveStatus({ skillId, routeEpoch, status });
        },
        []
    );

    const setSnapshotPending = useCallback(
        (pendingSkillId: number, pending: boolean) => {
            const current = pendingSnapshotSkillIdsRef.current;
            if (current.has(pendingSkillId) === pending) return;
            const next = new Set(current);
            if (pending) {
                next.add(pendingSkillId);
            } else {
                next.delete(pendingSkillId);
            }
            pendingSnapshotSkillIdsRef.current = next;
            setPendingSnapshotSkillIds(next);
        },
        []
    );
    useEffect(() => {
        if (routeSkillIdRef.current === skill.id) return;
        routeSkillIdRef.current = skill.id;
        routeEpochRef.current += 1;
    }, [skill.id]);

    useEffect(() => {
        if (lastSourceRef.current === skill) return;
        const current = stateRef.current;
        if (
            current.skillId === skill.id &&
            (pendingSaveRef.current?.skillId === current.skillId ||
                (saveConflictRef.current?.skillId === current.skillId &&
                    saveConflictRef.current.routeEpoch ===
                        routeEpochRef.current) ||
                (saveRecoveryRef.current?.skillId === current.skillId &&
                    saveRecoveryRef.current.routeEpoch ===
                        routeEpochRef.current) ||
                hasLocalEditorState(current) ||
                pendingSnapshotSkillIdsRef.current.has(current.skillId))
        ) {
            pendingSourceRef.current = skill;
            return;
        }
        lastSourceRef.current = skill;
        pendingSourceRef.current = null;
        transition((current) =>
            preserveSkillEditorClientKeys(
                current,
                current.skillId === skill.id
                    ? reseedSkillEditorState(current, skill)
                    : resetSkillEditorState(current, skill, {
                          reviewRequired: justDuplicated,
                      })
            )
        );
    }, [justDuplicated, skill, transition]);

    useEffect(() => {
        const pendingSource = pendingSourceRef.current;
        if (
            pendingSaveRef.current?.skillId === state.skillId ||
            saveConflict !== null ||
            saveRecovery !== null ||
            hasLocalState ||
            pendingSnapshotSkillIds.has(state.skillId) ||
            pendingSource === null ||
            pendingSource.id !== state.skillId
        ) {
            return;
        }
        pendingSourceRef.current = null;
        lastSourceRef.current = pendingSource;
        transition((current) =>
            preserveSkillEditorClientKeys(
                current,
                reseedSkillEditorState(current, pendingSource)
            )
        );
    }, [
        hasLocalState,
        pendingSaveSettlement,
        pendingSnapshotSkillIds,
        saveConflict,
        saveRecovery,
        state.skillId,
        transition,
    ]);

    useEffect(() => {
        if (!justDuplicated || clearedLocationKeyRef.current === location.key) {
            return;
        }
        clearedLocationKeyRef.current = location.key;
        navigate(
            {
                pathname: location.pathname,
                search: location.search,
                hash: location.hash,
            },
            { replace: true, state: null }
        );
    }, [
        justDuplicated,
        location.hash,
        location.key,
        location.pathname,
        location.search,
        navigate,
    ]);

    const scopeAssignment = getSkillEditorScopeAssignment(state.buffer.skill);
    const coreCountOrganizationId =
        state.buffer.skill.scopeKind === "organization"
            ? scopeAssignment.organizationId
            : null;
    const coreCountEnabled =
        state.buffer.skill.scopeKind !== "group" &&
        (state.buffer.skill.scopeKind !== "organization" ||
            coreCountOrganizationId !== null);
    const versionsQuery = useQuery({
        queryKey: ["admin-skill-versions", skill.id],
        queryFn: () => getSkillVersions(skill.id),
        retry: false,
    });
    const coreCountQuery = useQuery({
        queryKey: ["admin-skills-core-count", coreCountOrganizationId],
        queryFn: () => getCoreSkillCount(coreCountOrganizationId),
        enabled: coreCountEnabled,
        retry: false,
    });

    const cancelDetailsQuery = useCallback(
        (savedSkillId: number) =>
            queryClient.cancelQueries({
                queryKey: ["admin-skill-details", String(savedSkillId)],
                exact: true,
            }),
        [queryClient]
    );

    const invalidateAfterMutation = useCallback(
        async (savedSkillId: number, scopeChanged = false) => {
            await Promise.all([
                ...mutationInvalidationKeys.map((queryKey) =>
                    queryClient.invalidateQueries({ queryKey })
                ),
                queryClient.invalidateQueries({
                    queryKey: ["admin-skill-versions", savedSkillId],
                }),
                queryClient.invalidateQueries({
                    queryKey: ["admin-skill-details", String(savedSkillId)],
                    exact: true,
                    refetchType: "none",
                }),
                ...(scopeChanged
                    ? [
                          queryClient.invalidateQueries({
                              queryKey: organizationGroupQueryKeys.details,
                          }),
                      ]
                    : []),
            ]);
        },
        [queryClient]
    );

    const reconcileFailedSave = useCallback(
        (
            authoritative: ISkill,
            context: SkillEditorSaveFailureContext
        ): SkillEditorSaveRecovery | null => {
            if (
                stateRef.current.skillId !== context.skillId ||
                routeEpochRef.current !== context.routeEpoch
            ) {
                return null;
            }
            if (!context.failedOperation) {
                return createSkillEditorRetryBlockedRecovery(
                    context.completedOperations,
                    context.error
                );
            }
            const reconciled = reconcileSkillEditorSaveFailure(
                stateRef.current,
                authoritative,
                context.failedOperation
            );
            cacheAuthoritativeSkill(authoritative, context.routeEpoch);
            transition(() => reconciled.state);
            return reconciled.recovery
                ? {
                      ...reconciled.recovery,
                      completedOperations: [...context.completedOperations],
                  }
                : null;
        },
        [cacheAuthoritativeSkill, transition]
    );

    const executeSave = useSkillEditorSaveExecution({
        stateRef,
        routeEpochRef,
        pendingSourceRef,
        pendingSnapshotSkillIdsRef,
        saveRecoveryRef,
        acknowledgedConflictRef,
        referencesForState,
        transition,
        cacheAuthoritativeSkill,
        storeSaveConflict,
        storeSaveRecovery,
        storeSaveStatus,
        setSnapshotPending,
        cancelDetailsQuery,
        invalidateAfterMutation,
        reconcileFailedSave,
        preserveSkillEditorClientKeys,
        preserveIdlessSkillEditorFiles,
    });

    const saveMutation = useMutation({ mutationFn: executeSave });
    const saveErrorOwnerRef = useRef({
        skillId: state.skillId,
        routeEpoch: routeEpochRef.current,
    });
    const save = useCallback(
        (intent: SkillEditorSaveIntent) => {
            const pendingSkillId = stateRef.current.skillId;
            const pendingRouteEpoch = routeEpochRef.current;
            const inFlight = pendingSaveRef.current;
            if (inFlight) {
                const sameOwner =
                    inFlight.skillId === pendingSkillId &&
                    inFlight.routeEpoch === pendingRouteEpoch;
                if (sameOwner && inFlight.intent === intent) {
                    return inFlight.promise;
                }
                return Promise.reject(
                    new Error(
                        sameOwner
                            ? `Cannot start a ${intent} save while a ${inFlight.intent} save is in progress.`
                            : `Cannot start a ${intent} save while another save is in progress.`
                    )
                );
            }
            saveErrorOwnerRef.current = {
                skillId: pendingSkillId,
                routeEpoch: pendingRouteEpoch,
            };
            const pending = saveMutation.mutateAsync(intent);
            const clearPending = () => {
                if (pendingSaveRef.current?.promise === pending) {
                    pendingSaveRef.current = null;
                    setPendingSaveSettlement((current) => current + 1);
                }
            };
            pendingSaveRef.current = {
                skillId: pendingSkillId,
                routeEpoch: saveErrorOwnerRef.current.routeEpoch,
                intent,
                promise: pending,
            };
            pending.then(clearPending, clearPending);
            return pending;
        },
        [saveMutation]
    );

    const reloadConflicts = useCallback(() => {
        const ownedConflict = saveConflictRef.current;
        if (
            !ownedConflict ||
            ownedConflict.skillId !== stateRef.current.skillId ||
            ownedConflict.routeEpoch !== routeEpochRef.current
        ) {
            return;
        }
        acknowledgedConflictRef.current = null;
        transition((current) =>
            reloadSkillEditorConflicts(current, ownedConflict.conflict)
        );
        storeSaveConflict(null);
    }, [storeSaveConflict, transition]);

    const clearSaveConflict = useCallback(() => {
        acknowledgedConflictRef.current = null;
        storeSaveConflict(null);
    }, [storeSaveConflict]);

    const keepLocal = useCallback(
        (remoteToken: string) => {
            const ownedConflict = saveConflictRef.current;
            if (
                !ownedConflict ||
                ownedConflict.skillId !== stateRef.current.skillId ||
                ownedConflict.routeEpoch !== routeEpochRef.current ||
                ownedConflict.conflict.fence.token !== remoteToken
            ) {
                return Promise.reject(
                    new Error(
                        "The conflicting server version is no longer current"
                    )
                );
            }
            acknowledgedConflictRef.current = {
                skillId: ownedConflict.skillId,
                routeEpoch: ownedConflict.routeEpoch,
                token: remoteToken,
            };
            storeSaveConflict(null);
            return save(ownedConflict.intent);
        },
        [save, storeSaveConflict]
    );

    const reloadLatest = useCallback(async () => {
        const ownedRecovery = saveRecoveryRef.current;
        if (
            !ownedRecovery ||
            (ownedRecovery.recovery.kind !== "retry-blocked" &&
                ownedRecovery.recovery.kind !== "publish-status-unknown") ||
            ownedRecovery.skillId !== stateRef.current.skillId ||
            ownedRecovery.routeEpoch !== routeEpochRef.current
        ) {
            return null;
        }
        const resolutionOwner = {
            skillId: ownedRecovery.skillId,
            routeEpoch: ownedRecovery.routeEpoch,
        };
        const recoveryIsCurrent = () =>
            saveRecoveryRef.current === ownedRecovery &&
            stateRef.current.skillId === ownedRecovery.skillId &&
            routeEpochRef.current === ownedRecovery.routeEpoch;
        setRecoveryResolutionOwner(resolutionOwner);
        try {
            const authoritative = await getSkill(ownedRecovery.skillId);
            if (!recoveryIsCurrent()) return null;
            if (ownedRecovery.recovery.kind === "publish-status-unknown") {
                const outcome = reconcileSkillEditorUnknownPublication(
                    stateRef.current,
                    ownedRecovery.recovery,
                    authoritative
                );
                cacheAuthoritativeSkill(
                    authoritative,
                    ownedRecovery.routeEpoch
                );
                transition(() => outcome.state);
                storeSaveRecovery(
                    outcome.kind === "status-unknown" ||
                        outcome.kind === "unpublished-elsewhere"
                        ? {
                              ...ownedRecovery,
                              recovery: outcome.recovery,
                          }
                        : null
                );
                if (outcome.kind === "applied") {
                    saveMutation.reset();
                    storeSaveStatus(
                        { kind: "saved" },
                        ownedRecovery.skillId,
                        ownedRecovery.routeEpoch
                    );
                    await invalidateAfterMutation(ownedRecovery.skillId);
                }
                return outcome.kind === "applied"
                    ? "publish-applied"
                    : "recovery-updated";
            }
            const recovery = reconcileFailedSave(authoritative, ownedRecovery);
            storeSaveRecovery(recovery ? { ...ownedRecovery, recovery } : null);
            if (!recovery) saveMutation.reset();
            return "recovery-updated";
        } catch (error) {
            if (!recoveryIsCurrent()) return null;
            if (ownedRecovery.recovery.kind === "publish-status-unknown") {
                throw error;
            }
            storeSaveRecovery({
                ...ownedRecovery,
                recovery: createSkillEditorRetryBlockedRecovery(
                    ownedRecovery.completedOperations,
                    error
                ),
            });
            throw error;
        } finally {
            setRecoveryResolutionOwner((current) =>
                current?.skillId === resolutionOwner.skillId &&
                current.routeEpoch === resolutionOwner.routeEpoch
                    ? null
                    : current
            );
        }
    }, [
        cacheAuthoritativeSkill,
        invalidateAfterMutation,
        reconcileFailedSave,
        saveMutation,
        storeSaveRecovery,
        storeSaveStatus,
        transition,
    ]);

    const executePublishOnlyRetry = useCallback(
        async (
            ownedRecovery: OwnedSkillEditorSaveRecovery,
            retry: SkillEditorPublishOnlyRetryPlan
        ): Promise<SkillEditorPublishRetryResult | null> => {
            const resolutionOwner = {
                skillId: ownedRecovery.skillId,
                routeEpoch: ownedRecovery.routeEpoch,
            };
            const recoveryIsCurrent = () =>
                saveRecoveryRef.current === ownedRecovery &&
                stateRef.current.skillId === ownedRecovery.skillId &&
                routeEpochRef.current === ownedRecovery.routeEpoch;
            if (!recoveryIsCurrent()) return null;
            saveMutation.reset();
            setRecoveryResolutionOwner(resolutionOwner);
            try {
                const published = await publishSkill(ownedRecovery.skillId);
                if (recoveryIsCurrent()) {
                    cacheAuthoritativeSkill(
                        published,
                        ownedRecovery.routeEpoch
                    );
                    transition((current) =>
                        clearSkillEditorReviewRequired(
                            applySkillEditorOperationReceipt(current, {
                                kind: "publish",
                                operation: retry.attempt.operation,
                                skill: published,
                            })
                        )
                    );
                    storeSaveRecovery(null);
                    storeSaveStatus(
                        { kind: "saved" },
                        ownedRecovery.skillId,
                        ownedRecovery.routeEpoch
                    );
                }
                await invalidateAfterMutation(ownedRecovery.skillId);
                return { kind: "applied", skill: published };
            } catch (error) {
                const responseStatus = isAxiosError(error)
                    ? error.response?.status
                    : undefined;
                let authoritative: ISkill | null = null;
                try {
                    await invalidateAfterMutation(ownedRecovery.skillId);
                    authoritative = await getSkill(ownedRecovery.skillId);
                } catch {
                    authoritative = null;
                }
                if (!recoveryIsCurrent()) throw error;
                const outcome = evaluateSettledSkillEditorPublishFailure({
                    state: stateRef.current,
                    attempt: retry.attempt,
                    completedOperations: ownedRecovery.completedOperations,
                    error,
                    responseStatus,
                    authoritative,
                });
                if (authoritative) {
                    cacheAuthoritativeSkill(
                        authoritative,
                        ownedRecovery.routeEpoch
                    );
                }
                transition(() => outcome.state);
                storeSaveRecovery(
                    outcome.kind === "status-unknown" ||
                        outcome.kind === "draft-saved-publish-rejected" ||
                        outcome.kind === "unpublished-elsewhere"
                        ? {
                              ...ownedRecovery,
                              error,
                              completedOperations:
                                  ownedRecovery.completedOperations,
                              failedOperation: retry.attempt.operation,
                              recovery: outcome.recovery,
                          }
                        : null
                );
                if (outcome.kind === "applied") {
                    storeSaveStatus(
                        { kind: "saved" },
                        ownedRecovery.skillId,
                        ownedRecovery.routeEpoch
                    );
                    return { kind: "applied", skill: authoritative };
                }
                if (
                    outcome.kind === "rejected" ||
                    outcome.kind === "draft-saved-publish-rejected"
                ) {
                    return {
                        kind: "rejected",
                        reason: getSkillEditorErrorReason(error),
                    };
                }
                throw error;
            } finally {
                setRecoveryResolutionOwner((current) =>
                    current?.skillId === resolutionOwner.skillId &&
                    current.routeEpoch === resolutionOwner.routeEpoch
                        ? null
                        : current
                );
            }
        },
        [
            cacheAuthoritativeSkill,
            invalidateAfterMutation,
            storeSaveRecovery,
            storeSaveStatus,
            saveMutation,
            transition,
        ]
    );

    const retryPublishOnly = useCallback(
        (
            acknowledgment: SkillEditorPublishRetryWarningAcknowledgment
        ): Promise<SkillEditorPublishRetryResult | null> => {
            const pendingSkillId = stateRef.current.skillId;
            const pendingRouteEpoch = routeEpochRef.current;
            const inFlight = pendingPublishRetryRef.current;
            // A retry owned by a superseded route is stale, not competing: it
            // clears itself and its own ownership checks refuse to apply
            // anything, so it must not block the skill the user is now on.
            if (
                inFlight?.skillId === pendingSkillId &&
                inFlight.routeEpoch === pendingRouteEpoch
            ) {
                return inFlight.promise;
            }
            const ownedRecovery = saveRecoveryRef.current;
            if (
                !ownedRecovery ||
                ownedRecovery.recovery.kind !== "publish-status-unknown" ||
                ownedRecovery.skillId !== pendingSkillId ||
                ownedRecovery.routeEpoch !== pendingRouteEpoch ||
                !canRetrySkillEditorPublishOnly(ownedRecovery.recovery)
            ) {
                return Promise.resolve(null);
            }
            const retry = planSkillEditorPublishOnlyRetry(
                ownedRecovery.recovery,
                acknowledgment
            );
            if (!retry) return Promise.resolve(null);
            const pending = Promise.resolve().then(() =>
                executePublishOnlyRetry(ownedRecovery, retry)
            );
            pendingPublishRetryRef.current = {
                skillId: pendingSkillId,
                routeEpoch: pendingRouteEpoch,
                promise: pending,
            };
            const clearPending = () => {
                if (pendingPublishRetryRef.current?.promise === pending) {
                    pendingPublishRetryRef.current = null;
                }
            };
            pending.then(clearPending, clearPending);
            return pending;
        },
        [executePublishOnlyRetry]
    );

    const dismissPublishFailure = useCallback(() => {
        const ownedRecovery = saveRecoveryRef.current;
        if (
            !ownedRecovery ||
            (ownedRecovery.recovery.kind !== "draft-saved-publish-rejected" &&
                ownedRecovery.recovery.kind !== "publish-status-unknown" &&
                ownedRecovery.recovery.kind !==
                    "publish-unpublished-elsewhere") ||
            ownedRecovery.skillId !== stateRef.current.skillId ||
            ownedRecovery.routeEpoch !== routeEpochRef.current
        ) {
            return;
        }
        storeSaveRecovery(null);
        saveMutation.reset();
    }, [saveMutation, storeSaveRecovery]);

    const recreateUnderSameName = useCallback(
        (clientKey: string) => {
            const ownedRecovery = saveRecoveryRef.current;
            if (
                ownedRecovery?.recovery.kind !== "idless-local" ||
                ownedRecovery.recovery.clientKey !== clientKey ||
                ownedRecovery.skillId !== stateRef.current.skillId ||
                ownedRecovery.routeEpoch !== routeEpochRef.current
            ) {
                return;
            }
            storeSaveRecovery(null);
        },
        [storeSaveRecovery]
    );

    const renameAndRecreate = useCallback(
        (clientKey: string, name: string) => {
            const ownedRecovery = saveRecoveryRef.current;
            if (
                ownedRecovery?.recovery.kind !== "idless-local" ||
                ownedRecovery.recovery.clientKey !== clientKey ||
                ownedRecovery.skillId !== stateRef.current.skillId ||
                ownedRecovery.routeEpoch !== routeEpochRef.current
            ) {
                return;
            }
            transition((current) =>
                updateSkillEditorFile(current, clientKey, { name })
            );
            storeSaveRecovery(null);
        },
        [storeSaveRecovery, transition]
    );

    const discardLocal = useCallback(
        (clientKey: string) => {
            const ownedRecovery = saveRecoveryRef.current;
            if (
                ownedRecovery?.recovery.kind !== "idless-local" ||
                ownedRecovery.recovery.clientKey !== clientKey ||
                ownedRecovery.skillId !== stateRef.current.skillId ||
                ownedRecovery.routeEpoch !== routeEpochRef.current
            ) {
                return;
            }
            transition((current) => deleteSkillEditorFile(current, clientKey));
            storeSaveRecovery(null);
        },
        [storeSaveRecovery, transition]
    );

    const keepLocalAndRename = useCallback(
        (clientKey: string, name: string) => {
            const ownedRecovery = saveRecoveryRef.current;
            if (
                !ownedRecovery ||
                (ownedRecovery.recovery.kind !==
                    "idless-local-server-collision" &&
                    ownedRecovery.recovery.kind !== "server-file-collision") ||
                ownedRecovery.recovery.clientKey !== clientKey ||
                ownedRecovery.skillId !== stateRef.current.skillId ||
                ownedRecovery.routeEpoch !== routeEpochRef.current
            ) {
                return;
            }
            transition((current) =>
                updateSkillEditorFile(current, clientKey, { name })
            );
            storeSaveRecovery(null);
        },
        [storeSaveRecovery, transition]
    );

    const reloadServerFile = useCallback(
        async (clientKey: string) => {
            const ownedRecovery = saveRecoveryRef.current;
            if (
                !ownedRecovery ||
                (ownedRecovery.recovery.kind !==
                    "idless-local-server-collision" &&
                    ownedRecovery.recovery.kind !== "server-file-collision") ||
                ownedRecovery.recovery.clientKey !== clientKey ||
                ownedRecovery.skillId !== stateRef.current.skillId ||
                ownedRecovery.routeEpoch !== routeEpochRef.current
            ) {
                return;
            }
            const resolutionOwner = {
                skillId: ownedRecovery.skillId,
                routeEpoch: ownedRecovery.routeEpoch,
            };
            const recoveryIsCurrent = () =>
                saveRecoveryRef.current === ownedRecovery &&
                stateRef.current.skillId === ownedRecovery.skillId &&
                routeEpochRef.current === ownedRecovery.routeEpoch;
            setRecoveryResolutionOwner(resolutionOwner);
            try {
                const authoritative = await getSkill(ownedRecovery.skillId);
                if (!recoveryIsCurrent()) return;
                const targetId =
                    ownedRecovery.recovery.kind === "server-file-collision"
                        ? ownedRecovery.recovery.localServerId
                        : ownedRecovery.recovery.remoteServerId;
                const remoteFile = authoritative.skill_files.find(
                    ({ id }) => id === targetId
                );
                if (!remoteFile) {
                    throw new Error("The server file is no longer available");
                }
                cacheAuthoritativeSkill(
                    authoritative,
                    ownedRecovery.routeEpoch
                );
                transition((current) =>
                    reloadSkillEditorRecoveryFile(
                        current,
                        clientKey,
                        remoteFile
                    )
                );
                storeSaveRecovery(null);
            } catch (error) {
                if (!recoveryIsCurrent()) return;
                storeSaveRecovery({
                    ...ownedRecovery,
                    recovery: createSkillEditorRetryBlockedRecovery(
                        ownedRecovery.completedOperations,
                        error
                    ),
                });
                throw error;
            } finally {
                setRecoveryResolutionOwner((current) =>
                    current?.skillId === resolutionOwner.skillId &&
                    current.routeEpoch === resolutionOwner.routeEpoch
                        ? null
                        : current
                );
            }
        },
        [cacheAuthoritativeSkill, storeSaveRecovery, transition]
    );

    const updateFields = useCallback(
        (patch: Partial<SkillEditorFields>) => {
            transition((current) => updateSkillEditorFields(current, patch));
        },
        [transition]
    );
    const addFile = useCallback(() => {
        transition(addSkillEditorFile);
    }, [transition]);
    const updateFile = useCallback(
        (
            clientKey: string,
            patch: Partial<Pick<SkillEditorFile, "name" | "content">>
        ) => {
            transition((current) =>
                updateSkillEditorFile(current, clientKey, patch)
            );
        },
        [transition]
    );
    const deleteFile = useCallback(
        (clientKey: string) => {
            transition((current) => deleteSkillEditorFile(current, clientKey));
        },
        [transition]
    );
    const selectFile = useCallback(
        (clientKey: string) => {
            transition((current) => selectSkillEditorFile(current, clientKey));
        },
        [transition]
    );
    const requestRenameCommit = useCallback(
        (clientKey: string) => {
            transition((current) => {
                const file = current.buffer.files.find(
                    (candidate) => candidate.clientKey === clientKey
                );
                const referenceCount = file?.renameOrigin
                    ? getSkillEditorRenameReferenceCount(
                          referencesForState(current),
                          clientKey,
                          file.renameOrigin
                      )
                    : 0;
                return requestSkillEditorRenameCommit(
                    current,
                    clientKey,
                    referenceCount
                );
            });
        },
        [referencesForState, transition]
    );
    const acknowledgeRename = useCallback(
        (clientKey: string, destination: string) => {
            transition((current) =>
                acknowledgeSkillEditorRename(current, clientKey, destination)
            );
        },
        [transition]
    );
    const cancelRename = useCallback(
        (clientKey: string) => {
            transition((current) =>
                cancelSkillEditorRename(current, clientKey)
            );
        },
        [transition]
    );
    const discard = useCallback(() => {
        const pendingSource = pendingSourceRef.current;
        pendingSourceRef.current = null;
        lastSourceRef.current = pendingSource ?? skill;
        transition((current) =>
            preserveSkillEditorClientKeys(
                current,
                discardSkillEditorChanges(current, pendingSource ?? undefined)
            )
        );
    }, [skill, transition]);

    const displayFiles = useMemo(
        () => getSkillEditorDisplayFiles(state),
        [state]
    );
    const selectedFile = useMemo(
        () =>
            state.buffer.files.find(
                ({ clientKey }) => clientKey === state.selectedClientKey
            ) ?? null,
        [state.buffer.files, state.selectedClientKey]
    );
    const coreIncludesThisSkill =
        isLiveCoreSkill(skill) &&
        state.buffer.skill.scopeKind !== "group" &&
        skill.organization_group_id == null &&
        (coreCountOrganizationId === null
            ? skill.organization_id === null
            : skill.organization_id === null ||
              skill.organization_id === coreCountOrganizationId);
    const ownedSaveError =
        saveErrorOwnerRef.current.skillId === state.skillId &&
        saveErrorOwnerRef.current.routeEpoch === routeEpochRef.current
            ? saveMutation.error
            : null;
    const saveError =
        saveRecovery !== null ||
        ownedSaveError instanceof SkillEditorSaveConflictError ||
        ownedSaveError instanceof SkillEditorSaveRecoveryRequiredError ||
        ownedSaveError instanceof SkillEditorSaveValidationError
            ? null
            : ownedSaveError;
    const publishFailureReason =
        saveRecovery?.kind === "draft-saved-publish-rejected"
            ? getSkillEditorErrorReason(saveRecovery.error)
            : null;
    const canRetryPublishOnly =
        saveRecovery?.kind === "publish-status-unknown" &&
        canRetrySkillEditorPublishOnly(saveRecovery);

    return {
        skill,
        state,
        diff,
        saveChanges,
        validation,
        references,
        recognitionVocabulary,
        renameRequest: state.renameRequest,
        displayFiles,
        selectedFile,
        editorKey: getSkillEditorKey(state),
        isDirty,
        hasDiscardableChanges,
        hasPendingSnapshot,
        isSaving: saveMutation.isPending,
        isResolvingRecovery,
        saveConflict,
        saveRecovery,
        publishFailureReason,
        canRetryPublishOnly,
        saveStatus,
        saveError,
        refreshError:
            saveStatus.kind === "saved-with-refresh-warning"
                ? saveStatus.error
                : null,
        clearSaveError: saveMutation.reset,
        versions: versionsQuery.data,
        versionsError: versionsQuery.error,
        isVersionsLoading: versionsQuery.isPending,
        retryVersions: versionsQuery.refetch,
        coreCount: coreCountEnabled
            ? coreCountQuery.data?.coreCount
            : undefined,
        coreCountError: coreCountEnabled ? coreCountQuery.error : null,
        isCoreCountLoading: coreCountEnabled && coreCountQuery.isPending,
        retryCoreCount: coreCountQuery.refetch,
        coreCountScope:
            state.buffer.skill.scopeKind === "organization" ? "org" : "global",
        coreIncludesThisSkill,
        updateFields,
        addFile,
        updateFile,
        deleteFile,
        selectFile,
        requestRenameCommit,
        acknowledgeRename,
        cancelRename,
        getReferenceCount: (clientKey: string) =>
            getSkillEditorReferenceCount(references, clientKey),
        getInboundSources: (clientKey: string) =>
            getSkillEditorInboundSources(references, clientKey),
        getBrokenTargets: (clientKey: string) =>
            getSkillEditorBrokenTargets(references, clientKey),
        discard,
        save,
        reloadConflicts,
        clearSaveConflict,
        keepLocal,
        reloadLatest,
        retryPublishOnly,
        dismissPublishFailure,
        recreateUnderSameName,
        renameAndRecreate,
        discardLocal,
        reloadServerFile,
        keepLocalAndRename,
    };
};
